import React from 'react';
import {
  Typography,
  Box,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import TimelineIcon from '@mui/icons-material/Timeline';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { flightFileLabel, isFlightPlottable } from '../../utils/flightFileRefs';
import { sharedByLabel } from '../../utils/sharedFlights';
import { useTranslation } from '../../i18n';
import { Flight, FlightEditState, WeatherData } from './flightDetails.types';
import { FlightWeatherSection } from './FlightWeatherSection';
import { FlightAnalysisSection } from './FlightAnalysisSection';

type FieldOption = { id: string | number; label: string };

interface FlightDetailRowProps {
  flight: Flight;
  isThisFlightEditing: boolean;
  editingFlightId: string | null;
  editState: FlightEditState | null;
  setEditState: React.Dispatch<React.SetStateAction<FlightEditState | null>>;
  loadingOptions: boolean;
  flightTypeOptions: FieldOption[];
  pilotOptions: FieldOption[];
  payloadOptions: FieldOption[];
  customerOptions: FieldOption[];
  projectOptions: FieldOption[];
  airspaceObserverOptions: FieldOption[];
  payloadOperatorOptions: FieldOption[];
  weather: WeatherData | null;
  isFetchingWeather: boolean;
  isSaving: boolean;
  labelStyle: Record<string, unknown>;
  onStartEdit: (flight: Flight) => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onFetchWeather: (flightId: string) => void;
  onDownload: (downloadUrl: string, flightType: string) => void;
  onPlot: (flight: Flight) => void;
  onAddCustomer: () => void;
  onAddProject: () => void;
}

const formatHours = (hours: number | null): string => {
  if (hours === null || hours === undefined) return 'N/A';
  return `${hours.toFixed(2)}h`;
};

export const FlightDetailRow: React.FC<FlightDetailRowProps> = ({
  flight,
  isThisFlightEditing,
  editingFlightId,
  editState,
  setEditState,
  loadingOptions,
  flightTypeOptions,
  pilotOptions,
  payloadOptions,
  customerOptions,
  projectOptions,
  airspaceObserverOptions,
  payloadOperatorOptions,
  weather,
  isFetchingWeather,
  isSaving,
  labelStyle,
  onStartEdit,
  onCancelEdit,
  onSave,
  onFetchWeather,
  onDownload,
  onPlot,
  onAddCustomer,
  onAddProject,
}) => {
  // Source file name — from the ROW (#39 Phase C), via the shared helper the
  // other two filename surfaces already use. Disambiguates flights that share a
  // date, where the dialog title alone can't. The empty placeholder keeps
  // today's behaviour of hiding the field outright when there is no name;
  // rendering a bare uuid here is what #1294 reported as the visible symptom.
  const filename = flightFileLabel(flight, '');

  // A flight another organization shared is badged with its owner and offers what
  // the Shared view offers — download and Analytics — and nothing that writes (#1693).
  const { t } = useTranslation();
  const sharedBy = sharedByLabel(t, flight);

  const renderField = (
    label: string,
    value: string | null,
    editValue: string | undefined,
    options: FieldOption[],
    fieldKey: keyof FlightEditState,
    onAddNew?: () => void
  ) => {
    const isEditing = editingFlightId !== null && editState !== null;

    if (isEditing) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            value={editValue || ''}
            label={label}
            onChange={(e) =>
              setEditState((prev) => (prev ? { ...prev, [fieldKey]: e.target.value } : null))
            }
            disabled={loadingOptions}
          >
            <MenuItem value="">
              <em>Not specified</em>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.id} value={opt.id.toString()}>
                {opt.label}
              </MenuItem>
            ))}
            {onAddNew && (
              <>
                <Divider />
                <MenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAddNew();
                  }}
                  sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}
                >
                  + Add new...
                </MenuItem>
              </>
            )}
          </Select>
        </FormControl>
      );
    }

    return (
      <>
        <Typography variant="caption" sx={labelStyle}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ mt: 0.5 }}>
          {value || 'Not specified'}
        </Typography>
      </>
    );
  };

  return (
    <>
      {sharedBy && (
        <Chip
          label={sharedBy}
          size="small"
          data-testid="flight-shared-by"
          sx={{
            mb: 2,
            backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.15),
            color: 'text.primary',
            fontWeight: 500,
          }}
        />
      )}
      {/* Source file name — helps tell apart multiple flights on the same date */}
      {filename && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={labelStyle}>
            File
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}
          >
            {filename}
          </Typography>
        </Box>
      )}

      {/* Header with Edit button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="caption" sx={labelStyle}>
            Flight Type
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {isThisFlightEditing ? (
              // Single shared editState belongs to the one edited flight, so this
              // Select is gated on isThisFlightEditing (not the broader edit flag the
              // sibling fields use) — non-edited rows keep the read-only Chip.
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Flight Type</InputLabel>
                <Select
                  value={editState?.flight_type ?? ''}
                  label="Flight Type"
                  onChange={(e) =>
                    setEditState((prev) => (prev ? { ...prev, flight_type: e.target.value } : null))
                  }
                  disabled={loadingOptions}
                >
                  <MenuItem value="">
                    <em>Not specified</em>
                  </MenuItem>
                  {flightTypeOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id.toString()}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Chip
                label={flight.flight_type || 'Unknown'}
                size="small"
                sx={{
                  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                  color: BRAND_COLORS.forestGreen.main,
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
        </Box>
        {!editingFlightId && !flight.shared && (
          <IconButton
            onClick={() => onStartEdit(flight)}
            size="small"
            sx={{ color: BRAND_COLORS.forestGreen.main }}
          >
            <EditIcon />
          </IconButton>
        )}
      </Box>

      {/* Pilot and Payload */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          {renderField(
            'Pilot',
            flight.pilot_name,
            isThisFlightEditing ? editState?.pilot_id : undefined,
            pilotOptions,
            'pilot_id'
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          {renderField(
            'Payload',
            flight.payload_name,
            isThisFlightEditing ? editState?.payload_id : undefined,
            payloadOptions,
            'payload_id'
          )}
        </Box>
      </Box>

      {/* Customer and Project */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          {renderField(
            'Customer',
            flight.customer_name,
            isThisFlightEditing ? editState?.customer_id : undefined,
            customerOptions,
            'customer_id',
            onAddCustomer
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          {renderField(
            'Project',
            flight.project_name,
            isThisFlightEditing ? editState?.project_id : undefined,
            projectOptions,
            'project_id',
            onAddProject
          )}
        </Box>
      </Box>

      {/* Airspace Observer and Payload Operator */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          {renderField(
            'Airspace Observer',
            flight.airspace_observer_name,
            isThisFlightEditing ? editState?.airspace_observer_id : undefined,
            airspaceObserverOptions,
            'airspace_observer_id'
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          {renderField(
            'Payload Operator',
            flight.payload_operator_name,
            isThisFlightEditing ? editState?.payload_operator_id : undefined,
            payloadOperatorOptions,
            'payload_operator_id'
          )}
        </Box>
      </Box>

      {/* Flight Hours and Engine Hours (read-only) */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={labelStyle}>
            Flight Hours
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
            {formatHours(flight.flight_hours)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={labelStyle}>
            Engine Hours
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
            {formatHours(flight.engine_hours)}
          </Typography>
        </Box>
      </Box>

      {/* Weather Section */}
      <FlightWeatherSection
        weather={weather}
        isFetching={isFetchingWeather}
        onFetch={flight.shared ? undefined : () => onFetchWeather(flight.flight_id)}
        labelStyle={labelStyle}
      />

      {/* Flight Analysis Results.
          Gated on the registry row, not on the storage key (STD-029 D-8): the
          verdict is keyed on `object_id`, so a flight without one has nothing
          to fetch and the section hides rather than rendering an inert
          "no analysis" disclosure — the same degradation the grid badge takes
          for the same reason (R-9). */}
      {flight.object_id && (
        <FlightAnalysisSection objectId={flight.object_id} labelStyle={labelStyle} />
      )}

      {/* Edit actions or Download button */}
      {isThisFlightEditing ? (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button variant="outlined" onClick={onCancelEdit} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={isSaving}
            sx={{
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8),
              },
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {flight.download_url && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => onDownload(flight.download_url, flight.flight_type)}
              sx={{
                borderColor: BRAND_COLORS.yellow.main,
                color: BRAND_COLORS.yellow.main,
                '&:hover': {
                  borderColor: BRAND_COLORS.yellow.main,
                  backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.04),
                },
              }}
            >
              Download Flight Data
            </Button>
          )}
          {isFlightPlottable(flight) && (
            <Button
              variant="outlined"
              startIcon={<TimelineIcon />}
              onClick={() => onPlot(flight)}
              sx={{
                borderColor: BRAND_COLORS.forestGreen.main,
                color: BRAND_COLORS.forestGreen.main,
                '&:hover': {
                  borderColor: BRAND_COLORS.forestGreen.main,
                  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04),
                },
              }}
            >
              Plot
            </Button>
          )}
        </Box>
      )}
    </>
  );
};
